import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OutsourcingClients() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  // Detail / Edit
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('details');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:'', code:'', client_phone:'', client_email:'', contact_person_phone:'', contact_person_email:'', status:'draft', notes:'' });

  // Sub-data
  const [contacts, setContacts] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [clientServices, setClientServices] = useState([]);

  // Sub-forms
  const [contactForm, setContactForm] = useState({ name:'', email:'', phone:'', contact_type:'main', is_primary:false, notes:'' });
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const [supervisorForm, setSupervisorForm] = useState({ user_id:'', role:'supervisor' });
  const [showSupervisorForm, setShowSupervisorForm] = useState(false);

  const [serviceForm, setServiceForm] = useState({ outsourcing_service_id:'', current_price:'', effective_start_date:'', notes:'' });
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);

  useEffect(() => { if (companyId) fetchClients(); }, [companyId, page, statusFilter]);

  const authFetch = async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: { ...getAuthHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return null; }
    return res;
  };

  const fetchClients = async () => {
    setLoading(true);
    let url = `${API}/companies/${companyId}/outsourcing/clients?page=${page}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (statusFilter) url += `&status=${statusFilter}`;
    const res = await authFetch(url);
    if (!res) return;
    const json = await res.json();
    setClients(json.data || []);
    setLastPage(json.last_page || 1);
    setLoading(false);
  };

  const openClient = async (client) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${client.id}`);
    if (!res) return;
    setSelected(await res.json());
    setTab('details');
    loadContacts(client.id);
    loadSupervisors(client.id);
    loadDocuments(client.id);
    loadClientServices(client.id);
  };

  const loadContacts = async (id) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${id}/contacts`);
    if (res) setContacts(await res.json());
  };
  const loadSupervisors = async (id) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${id}/supervisors`);
    if (res) setSupervisors(await res.json());
  };
  const loadDocuments = async (id) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${id}/documents`);
    if (res) setDocuments(await res.json());
  };
  const loadClientServices = async (id) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${id}/services`);
    if (res) setClientServices(await res.json());
  };
  const loadAvailableServices = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/services?status=active`);
    if (res) { const j = await res.json(); setAvailableServices(j.data || []); }
  };

  // ── CRUD ──
  const saveClient = async () => {
    const method = selected ? 'PUT' : 'POST';
    const url = selected ? `${API}/companies/${companyId}/outsourcing/clients/${selected.id}` : `${API}/companies/${companyId}/outsourcing/clients`;
    const res = await authFetch(url, { method, headers:{'Content-Type':'application/json'}, body: JSON.stringify(form) });
    if (!res) return;
    if (res.ok) { setShowForm(false); setSelected(null); fetchClients(); }
    else { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  const archiveClient = async (id) => {
    if (!window.confirm('Archive this client?')) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${id}/archive`, { method:'POST' });
    if (res && res.ok) { setSelected(null); fetchClients(); }
  };

  const deleteClient = async (id) => {
    if (!window.confirm('Delete this client? This cannot be undone.')) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${id}`, { method:'DELETE' });
    if (res && res.ok) { setSelected(null); fetchClients(); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  // ── CONTACTS ──
  const saveContact = async () => {
    const url = editingContact
      ? `${API}/companies/${companyId}/outsourcing/clients/${selected.id}/contacts/${editingContact.id}`
      : `${API}/companies/${companyId}/outsourcing/clients/${selected.id}/contacts`;
    const res = await authFetch(url, { method: editingContact ? 'PUT' : 'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(contactForm) });
    if (res && res.ok) { setShowContactForm(false); setEditingContact(null); setContactForm({ name:'',email:'',phone:'',contact_type:'main',is_primary:false,notes:'' }); loadContacts(selected.id); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${selected.id}/contacts/${contactId}`, { method:'DELETE' });
    if (res && res.ok) loadContacts(selected.id);
  };

  // ── SUPERVISORS ──
  const addSupervisor = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${selected.id}/supervisors`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(supervisorForm) });
    if (res && res.ok) { setShowSupervisorForm(false); setSupervisorForm({user_id:'',role:'supervisor'}); loadSupervisors(selected.id); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  const removeSupervisor = async (supId) => {
    if (!window.confirm('Remove this supervisor?')) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${selected.id}/supervisors/${supId}`, { method:'DELETE' });
    if (res && res.ok) loadSupervisors(selected.id);
  };

  // ── DOCUMENTS ──
  const uploadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', file.name);
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}/companies/${companyId}/outsourcing/clients/${selected.id}/documents`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
    });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
    if (res.ok) loadDocuments(selected.id);
    else { const err = await res.json(); window.alert(err.message || 'Upload failed'); }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${selected.id}/documents/${docId}`, { method:'DELETE' });
    if (res && res.ok) loadDocuments(selected.id);
  };

  // ── CLIENT SERVICES ──
  const addClientService = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${selected.id}/services`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(serviceForm) });
    if (res && res.ok) { setShowServiceForm(false); setServiceForm({outsourcing_service_id:'',current_price:'',effective_start_date:'',notes:''}); loadClientServices(selected.id); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  const endClientService = async (csId) => {
    if (!window.confirm('End this client service?')) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${selected.id}/services/${csId}/end`, { method:'POST' });
    if (res && res.ok) loadClientServices(selected.id);
  };

  if (!companyId) return <div style={s.page}><p>Please select a company first.</p></div>;

  // ── DETAIL VIEW ──
  if (selected) {
    return (
      <div style={s.page}>
        <button onClick={() => setSelected(null)} style={s.backBtn}>← Back to Clients</button>
        <div style={s.detailHeader}>
          <h2 style={s.title}>{selected.name} <span style={{fontSize:14,color:'#999'}}>({selected.code})</span></h2>
          <div style={{display:'flex',gap:8}}>
            <span style={{...s.badge, backgroundColor: statusClr(selected.status)}}>{selected.status}</span>
            <button onClick={() => { setForm({name:selected.name,code:selected.code,client_phone:selected.client_phone||'',client_email:selected.client_email||'',contact_person_phone:selected.contact_person_phone||'',contact_person_email:selected.contact_person_email||'',status:selected.status,notes:selected.notes||''}); setShowForm(true); }} style={s.btnPrimary}>Edit</button>
            {selected.status !== 'archived' && <button onClick={() => archiveClient(selected.id)} style={s.btnWarn}>Archive</button>}
            {selected.status === 'draft' && <button onClick={() => deleteClient(selected.id)} style={s.btnDanger}>Delete</button>}
          </div>
        </div>

        {/* Tabs */}
        <div style={s.tabs}>
          {['details','contacts','supervisors','documents','services'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={tab === t ? s.tabActive : s.tab}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'details' && (
          <div style={s.section}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
              <p style={{margin:0}}><strong>Client Phone:</strong> {selected.client_phone || '—'}</p>
              <p style={{margin:0}}><strong>Client Email:</strong> {selected.client_email || '—'}</p>
              <p style={{margin:0}}><strong>Contact Person Phone:</strong> {selected.contact_person_phone || '—'}</p>
              <p style={{margin:0}}><strong>Contact Person Email:</strong> {selected.contact_person_email || '—'}</p>
            </div>
            <p><strong>Notes:</strong> {selected.notes || '—'}</p>
            <p><strong>Accounting Customer ID:</strong> {selected.accounting_customer_id || '—'}</p>
            <p><strong>Created:</strong> {new Date(selected.created_at).toLocaleDateString()}</p>
          </div>
        )}

        {tab === 'contacts' && (
          <div style={s.section}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
              <h3 style={{margin:0}}>Contacts</h3>
              <button onClick={() => { setContactForm({name:'',email:'',phone:'',contact_type:'main',is_primary:false,notes:''}); setEditingContact(null); setShowContactForm(true); }} style={s.btnPrimary}>+ Add Contact</button>
            </div>
            {contacts.length === 0 && <p style={{color:'#999'}}>No contacts.</p>}
            {contacts.map(c => (
              <div key={c.id} style={s.listItem}>
                <div>
                  <strong>{c.name}</strong> {c.is_primary ? <span style={{...s.badge,backgroundColor:'#27ae60',fontSize:11}}>Primary</span> : null}
                  <div style={{fontSize:13,color:'#666'}}>{c.contact_type} · {c.email || '—'} · {c.phone || '—'}</div>
                  {c.notes && <div style={{fontSize:12,color:'#999'}}>{c.notes}</div>}
                </div>
                <div style={{display:'flex',gap:6}}>
                  <button onClick={() => { setEditingContact(c); setContactForm({name:c.name,email:c.email||'',phone:c.phone||'',contact_type:c.contact_type,is_primary:c.is_primary,notes:c.notes||''}); setShowContactForm(true); }} style={s.btnSm}>Edit</button>
                  <button onClick={() => deleteContact(c.id)} style={{...s.btnSm,color:'#e74c3c'}}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'supervisors' && (
          <div style={s.section}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
              <h3 style={{margin:0}}>Internal Supervisors</h3>
              <button onClick={() => { setSupervisorForm({user_id:'',role:'supervisor'}); setShowSupervisorForm(true); }} style={s.btnPrimary}>+ Add Supervisor</button>
            </div>
            {supervisors.length === 0 && <p style={{color:'#999'}}>No supervisors assigned.</p>}
            {supervisors.map(sv => (
              <div key={sv.id} style={s.listItem}>
                <div><strong>{sv.user?.name || `User #${sv.user_id}`}</strong> <span style={{fontSize:13,color:'#666'}}>({sv.role})</span></div>
                <button onClick={() => removeSupervisor(sv.id)} style={{...s.btnSm,color:'#e74c3c'}}>Remove</button>
              </div>
            ))}
          </div>
        )}

        {tab === 'documents' && (
          <div style={s.section}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
              <h3 style={{margin:0}}>Documents</h3>
              <label style={s.btnPrimary}>
                Upload <input type="file" onChange={uploadDocument} style={{display:'none'}} />
              </label>
            </div>
            {documents.length === 0 && <p style={{color:'#999'}}>No documents.</p>}
            {documents.map(d => (
              <div key={d.id} style={s.listItem}>
                <div>
                  <strong>{d.name}</strong>
                  {d.expiry_date && <span style={{fontSize:12,color: new Date(d.expiry_date) < new Date() ? '#e74c3c' : '#999', marginLeft:8}}>Expires: {d.expiry_date}</span>}
                  <div style={{fontSize:12,color:'#999'}}>Uploaded by: {d.uploader?.name || '—'}</div>
                </div>
                <button onClick={() => deleteDocument(d.id)} style={{...s.btnSm,color:'#e74c3c'}}>Delete</button>
              </div>
            ))}
          </div>
        )}

        {tab === 'services' && (
          <div style={s.section}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
              <h3 style={{margin:0}}>Service Catalog</h3>
              <button onClick={() => { loadAvailableServices(); setServiceForm({outsourcing_service_id:'',current_price:'',effective_start_date:'',notes:''}); setShowServiceForm(true); }} style={s.btnPrimary}>+ Add Service</button>
            </div>
            {clientServices.length === 0 && <p style={{color:'#999'}}>No services assigned.</p>}
            {clientServices.map(cs => (
              <div key={cs.id} style={s.listItem}>
                <div>
                  <strong>{cs.service?.name || '—'}</strong> <span style={{fontSize:12,color:'#999'}}>({cs.service?.code})</span>
                  <div style={{fontSize:13,color:'#666'}}>Price: ${cs.current_price} · Status: {cs.status} · Since: {cs.effective_start_date || '—'}</div>
                </div>
                {cs.status === 'active' && <button onClick={() => endClientService(cs.id)} style={{...s.btnSm,color:'#e74c3c'}}>End</button>}
              </div>
            ))}
          </div>
        )}

        {/* Modals */}
        {showForm && renderModal('Client', () => setShowForm(false), <>
          <label style={s.label}>Name *</label>
          <input value={form.name} onChange={e => setForm({...form,name:e.target.value})} style={s.input} />
          <label style={s.label}>Code</label>
          <input value={form.code} onChange={e => setForm({...form,code:e.target.value})} style={s.input} placeholder="Auto-generated if empty" />
          <label style={s.label}>Client Phone *</label>
          <input value={form.client_phone} onChange={e => setForm({...form,client_phone:e.target.value})} style={s.input} placeholder="e.g. +961 1 234 567" />
          <label style={s.label}>Client Email *</label>
          <input type="email" value={form.client_email} onChange={e => setForm({...form,client_email:e.target.value})} style={s.input} placeholder="e.g. info@client.com" />
          <label style={s.label}>Contact Person Phone *</label>
          <input value={form.contact_person_phone} onChange={e => setForm({...form,contact_person_phone:e.target.value})} style={s.input} placeholder="e.g. +961 3 456 789" />
          <label style={s.label}>Contact Person Email *</label>
          <input type="email" value={form.contact_person_email} onChange={e => setForm({...form,contact_person_email:e.target.value})} style={s.input} placeholder="e.g. john@client.com" />
          <label style={s.label}>Status</label>
          <select value={form.status} onChange={e => setForm({...form,status:e.target.value})} style={s.input}>
            {['draft','active','on_hold','ended','archived'].map(st => <option key={st} value={st}>{st}</option>)}
          </select>
          <label style={s.label}>Notes</label>
          <textarea value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} style={{...s.input,minHeight:60}} />
          <button onClick={saveClient} style={s.btnPrimary}>Save</button>
        </>)}

        {showContactForm && renderModal(editingContact ? 'Edit Contact' : 'Add Contact', () => { setShowContactForm(false); setEditingContact(null); }, <>
          <label style={s.label}>Name *</label>
          <input value={contactForm.name} onChange={e => setContactForm({...contactForm,name:e.target.value})} style={s.input} />
          <label style={s.label}>Email</label>
          <input value={contactForm.email} onChange={e => setContactForm({...contactForm,email:e.target.value})} style={s.input} />
          <label style={s.label}>Phone</label>
          <input value={contactForm.phone} onChange={e => setContactForm({...contactForm,phone:e.target.value})} style={s.input} />
          <label style={s.label}>Type</label>
          <select value={contactForm.contact_type} onChange={e => setContactForm({...contactForm,contact_type:e.target.value})} style={s.input}>
            {['main','operational','billing','other'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <label style={{...s.label,display:'flex',alignItems:'center',gap:8}}>
            <input type="checkbox" checked={contactForm.is_primary} onChange={e => setContactForm({...contactForm,is_primary:e.target.checked})} /> Primary Contact
          </label>
          <label style={s.label}>Notes</label>
          <textarea value={contactForm.notes} onChange={e => setContactForm({...contactForm,notes:e.target.value})} style={{...s.input,minHeight:50}} />
          <button onClick={saveContact} style={s.btnPrimary}>Save</button>
        </>)}

        {showSupervisorForm && renderModal('Add Supervisor', () => setShowSupervisorForm(false), <>
          <label style={s.label}>User ID *</label>
          <input type="number" value={supervisorForm.user_id} onChange={e => setSupervisorForm({...supervisorForm,user_id:e.target.value})} style={s.input} />
          <label style={s.label}>Role</label>
          <select value={supervisorForm.role} onChange={e => setSupervisorForm({...supervisorForm,role:e.target.value})} style={s.input}>
            <option value="supervisor">Supervisor</option>
            <option value="account_manager">Account Manager</option>
          </select>
          <button onClick={addSupervisor} style={s.btnPrimary}>Add</button>
        </>)}

        {showServiceForm && renderModal('Add Service to Client', () => setShowServiceForm(false), <>
          <label style={s.label}>Service *</label>
          <select value={serviceForm.outsourcing_service_id} onChange={e => setServiceForm({...serviceForm,outsourcing_service_id:e.target.value})} style={s.input}>
            <option value="">Select...</option>
            {availableServices.map(sv => <option key={sv.id} value={sv.id}>{sv.name} ({sv.code})</option>)}
          </select>
          <label style={s.label}>Price</label>
          <input type="number" step="0.01" value={serviceForm.current_price} onChange={e => setServiceForm({...serviceForm,current_price:e.target.value})} style={s.input} />
          <label style={s.label}>Effective Start Date</label>
          <input type="date" value={serviceForm.effective_start_date} onChange={e => setServiceForm({...serviceForm,effective_start_date:e.target.value})} style={s.input} />
          <label style={s.label}>Notes</label>
          <textarea value={serviceForm.notes} onChange={e => setServiceForm({...serviceForm,notes:e.target.value})} style={{...s.input,minHeight:50}} />
          <button onClick={addClientService} style={s.btnPrimary}>Add</button>
        </>)}
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div style={s.page}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12,marginBottom:16}}>
        <h2 style={s.title}>Outsourcing Clients</h2>
        <button onClick={() => { setForm({name:'',code:'',client_phone:'',client_email:'',contact_person_phone:'',contact_person_email:'',status:'draft',notes:''}); setSelected(null); setShowForm(true); }} style={s.btnPrimary}>+ New Client</button>
      </div>

      <div style={s.filterRow}>
        <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key==='Enter' && fetchClients()} style={s.input} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{...s.input,width:140}}>
          <option value="">All Statuses</option>
          {['draft','active','on_hold','ended','archived'].map(st => <option key={st} value={st}>{st}</option>)}
        </select>
        <button onClick={() => { setPage(1); fetchClients(); }} style={s.btnPrimary}>Search</button>
      </div>

      {initialLoading ? <p>Loading...</p> : clients.length === 0 ? <p style={{color:'#999'}}>No clients found.</p> : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Name</th>
                <th style={s.th}>Code</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Employees</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c, i) => (
                <tr key={c.id} style={i%2===0 ? s.rowEven : s.rowOdd}>
                  <td style={s.td}><span style={{cursor:'pointer',color:'#2980b9',fontWeight:600}} onClick={() => openClient(c)}>{c.name}</span></td>
                  <td style={s.td}>{c.code}</td>
                  <td style={s.td}><span style={{...s.badge, backgroundColor:statusClr(c.status)}}>{c.status}</span></td>
                  <td style={s.td}>{c.active_assignments_count ?? '—'}</td>
                  <td style={s.td}>
                    <button onClick={() => openClient(c)} style={s.btnSm}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 && (
        <div style={s.pagination}>
          <button disabled={page<=1} onClick={() => setPage(page-1)} style={s.pageBtn}>Prev</button>
          <span>Page {page} of {lastPage}</span>
          <button disabled={page>=lastPage} onClick={() => setPage(page+1)} style={s.pageBtn}>Next</button>
        </div>
      )}

      {showForm && !selected && renderModal('New Client', () => setShowForm(false), <>
        <label style={s.label}>Name *</label>
        <input value={form.name} onChange={e => setForm({...form,name:e.target.value})} style={s.input} />
        <label style={s.label}>Code</label>
        <input value={form.code} onChange={e => setForm({...form,code:e.target.value})} style={s.input} placeholder="Auto-generated if empty" />
        <label style={s.label}>Client Phone *</label>
        <input value={form.client_phone} onChange={e => setForm({...form,client_phone:e.target.value})} style={s.input} placeholder="e.g. +961 1 234 567" />
        <label style={s.label}>Client Email *</label>
        <input type="email" value={form.client_email} onChange={e => setForm({...form,client_email:e.target.value})} style={s.input} placeholder="e.g. info@client.com" />
        <label style={s.label}>Contact Person Phone *</label>
        <input value={form.contact_person_phone} onChange={e => setForm({...form,contact_person_phone:e.target.value})} style={s.input} placeholder="e.g. +961 3 456 789" />
        <label style={s.label}>Contact Person Email *</label>
        <input type="email" value={form.contact_person_email} onChange={e => setForm({...form,contact_person_email:e.target.value})} style={s.input} placeholder="e.g. john@client.com" />
        <label style={s.label}>Status</label>
        <select value={form.status} onChange={e => setForm({...form,status:e.target.value})} style={s.input}>
          {['draft','active'].map(st => <option key={st} value={st}>{st}</option>)}
        </select>
        <label style={s.label}>Notes</label>
        <textarea value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} style={{...s.input,minHeight:60}} />
        <button onClick={saveClient} style={s.btnPrimary}>Create</button>
      </>)}
    </div>
  );
}

function renderModal(title, onClose, children) {
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <h3 style={{margin:0}}>{title}</h3>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:20,cursor:'pointer'}}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function statusClr(st) {
  const m = { draft:'#95a5a6', active:'#27ae60', on_hold:'#f39c12', ended:'#e74c3c', archived:'#7f8c8d' };
  return m[st] || '#95a5a6';
}

const s = {
  page: { padding:24, maxWidth:1200, margin:'0 auto' },
  title: { fontSize:24, fontWeight:700, margin:0 },
  filterRow: { display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' },
  input: { padding:'8px 12px', borderRadius:6, border:'1px solid #ccc', fontSize:14, width:'100%', boxSizing:'border-box', marginBottom:8 },
  label: { display:'block', fontSize:13, fontWeight:600, marginBottom:4, marginTop:8 },
  tableWrap: { overflowX:'auto' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'10px 12px', borderBottom:'2px solid #3498db', fontSize:13, fontWeight:600, whiteSpace:'nowrap' },
  td: { padding:'10px 12px', borderBottom:'1px solid #eee', fontSize:14 },
  rowEven: { background:'#fafafa' },
  rowOdd: { background:'#fff' },
  badge: { display:'inline-block', padding:'3px 10px', borderRadius:12, color:'#fff', fontSize:12, fontWeight:600, textTransform:'capitalize' },
  btnPrimary: { padding:'8px 16px', background:'#3498db', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:14, fontWeight:600 },
  btnWarn: { padding:'8px 16px', background:'#f39c12', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:14 },
  btnDanger: { padding:'8px 16px', background:'#e74c3c', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:14 },
  btnSm: { padding:'4px 10px', background:'#ecf0f1', border:'none', borderRadius:4, cursor:'pointer', fontSize:13 },
  backBtn: { background:'none', border:'none', color:'#3498db', cursor:'pointer', fontSize:14, fontWeight:600, marginBottom:12, padding:0 },
  detailHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:16 },
  tabs: { display:'flex', gap:0, borderBottom:'2px solid #ecf0f1', marginBottom:16 },
  tab: { padding:'10px 20px', background:'none', border:'none', cursor:'pointer', fontSize:14, color:'#999', borderBottom:'2px solid transparent' },
  tabActive: { padding:'10px 20px', background:'none', border:'none', cursor:'pointer', fontSize:14, color:'#3498db', fontWeight:600, borderBottom:'2px solid #3498db' },
  section: { background:'#fff', borderRadius:10, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.08)', marginBottom:16 },
  listItem: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #f0f0f0', flexWrap:'wrap', gap:8 },
  overlay: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal: { background:'#fff', borderRadius:12, padding:24, width:'90%', maxWidth:500, maxHeight:'85vh', overflowY:'auto' },
  pagination: { display:'flex', justifyContent:'center', alignItems:'center', gap:16, marginTop:16 },
  pageBtn: { padding:'6px 14px', border:'1px solid #ccc', borderRadius:6, background:'#fff', cursor:'pointer' },
};
